import { useRef } from 'react';

interface Props {
  value: number;
  min?: number;
  max?: number;
  onChange: (v: number) => void;
  size?: number;
  title?: string;
  /** Logarithmic scale (for frequencies, etc.). */
  log?: boolean;
}

/** Rotary knob: drag vertically, double-click to reset to center (or min). */
export function Knob({ value, min = -1, max = 1, onChange, size = 26, title, log = false }: Props) {
  const drag = useRef<{ startY: number; startValue: number } | null>(null);

  const toT = (v: number) =>
    log ? (Math.log(v) - Math.log(min)) / (Math.log(max) - Math.log(min)) : (v - min) / (max - min);
  const fromT = (t: number) =>
    log ? Math.exp(Math.log(min) + t * (Math.log(max) - Math.log(min))) : min + t * (max - min);

  const t = Math.max(0, Math.min(1, toT(value)));
  const angle = -135 + t * 270;

  return (
    <div
      className="knob"
      style={{ width: size, height: size }}
      title={title}
      onPointerDown={(e) => {
        drag.current = { startY: e.clientY, startValue: value };
        e.currentTarget.setPointerCapture(e.pointerId);
      }}
      onPointerMove={(e) => {
        if (!drag.current) return;
        const dy = drag.current.startY - e.clientY;
        const t0 = toT(drag.current.startValue);
        let v = fromT(Math.max(0, Math.min(1, t0 + dy / 110)));
        if (!log && min < 0 && max > 0 && Math.abs(v) < 0.08) v = 0; // center snap
        onChange(Math.min(max, Math.max(min, v)));
      }}
      onPointerUp={() => {
        drag.current = null;
      }}
      onDoubleClick={() => onChange(min < 0 && max > 0 ? 0 : min)}
    >
      <div className="knob-dial" style={{ transform: `rotate(${angle}deg)` }}>
        <div className="knob-indicator" />
      </div>
    </div>
  );
}